import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WizardSelectOption } from '../components/ui/WizardSelect';
import {
  getCategoryAllocationUsers,
  getCategoryAllocations,
  type CategoryAllocationUserRow,
} from '../services/caseRequestCategoryAllocation';
import { getFileCategories, getSubFileCategories } from '../services/criminalCaseRequests';
import type { LookupItem } from '../mocks/criminalMasterData';
import {
  ensureExemptSubFileCategoryIdsLoaded,
  fileCategoryExemptFromSubFileCategory,
  normalizeFileCategoryLabel,
} from '../utils/subFileCategoryRules';
import {
  filterAssignableCounselUsers,
  formatCategoryAllocationUserLabel,
} from '../utils/configureAssignmentFields';

type LoadState = 'idle' | 'loading' | 'error';

function findLookupByName(items: LookupItem[], name: string): LookupItem | null {
  const target = normalizeFileCategoryLabel(name).toLowerCase();
  if (!target) return null;
  return items.find((item) => normalizeFileCategoryLabel(item.name).toLowerCase() === target) ?? null;
}

async function resolveAssignTaskCategoryIds(input: {
  fileCategoryId: string;
  subFileCategoryId: string;
  fileCategoryName: string;
  subFileCategoryName: string;
}): Promise<{ categoryId: string; subCategoryId: string } | null> {
  let categoryId = input.fileCategoryId.trim();
  let subCategoryId = input.subFileCategoryId.trim();
  const categoryName = normalizeFileCategoryLabel(input.fileCategoryName);
  const subCategoryName = normalizeFileCategoryLabel(input.subFileCategoryName);

  if (!categoryId && categoryName) {
    const catsRes = await getFileCategories();
    if (!catsRes.success) return null;
    categoryId = findLookupByName(catsRes.data, categoryName)?.id ?? '';
  }

  const exemptFromSubCategory = fileCategoryExemptFromSubFileCategory({
    id: categoryId,
    name: categoryName,
  });

  if (!subCategoryId && subCategoryName && categoryId && !exemptFromSubCategory) {
    const subsRes = await getSubFileCategories(categoryId);
    if (!subsRes.success) return null;
    subCategoryId = findLookupByName(subsRes.data, subCategoryName)?.id ?? '';
  }

  if (!categoryId || (!subCategoryId && !exemptFromSubCategory)) return null;
  return { categoryId, subCategoryId };
}

export function useCategoryAllocationCounselOptions(input: {
  active: boolean;
  fileCategoryId: string;
  subFileCategoryId: string;
  fileCategoryName: string;
  subFileCategoryName: string;
}) {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [allocationUsers, setAllocationUsers] = useState<CategoryAllocationUserRow[]>([]);
  const [selectedCounselId, setSelectedCounselId] = useState('');

  const reset = useCallback(() => {
    setLoadState('idle');
    setAllocationUsers([]);
    setSelectedCounselId('');
  }, []);

  useEffect(() => {
    if (!input.active) {
      reset();
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      await ensureExemptSubFileCategoryIdsLoaded();
      if (controller.signal.aborted) return;

      const resolved = await resolveAssignTaskCategoryIds({
        fileCategoryId: input.fileCategoryId,
        subFileCategoryId: input.subFileCategoryId,
        fileCategoryName: input.fileCategoryName,
        subFileCategoryName: input.subFileCategoryName,
      });
      if (controller.signal.aborted) return;

      if (!resolved) {
        setLoadState('idle');
        setAllocationUsers([]);
        return;
      }

      const { categoryId, subCategoryId } = resolved;
      const categoryName = normalizeFileCategoryLabel(input.fileCategoryName);

      setLoadState('loading');
      setAllocationUsers([]);
      setSelectedCounselId('');

      const allocationsRes = await getCategoryAllocations({
        fileCategoryId: categoryId,
        subFileCategoryId: subCategoryId,
        fileCategoryName: categoryName,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!allocationsRes.success) {
        setLoadState('error');
        return;
      }

      const allocationIds = allocationsRes.data.map((row) => row.id).filter(Boolean);
      if (!allocationIds.length) {
        setLoadState('idle');
        setAllocationUsers([]);
        return;
      }

      const results = await Promise.all(
        allocationIds.map((id) =>
          getCategoryAllocationUsers({ requestCategoryAllocationId: id, signal: controller.signal }),
        ),
      );
      if (controller.signal.aborted) return;

      const merged: CategoryAllocationUserRow[] = [];
      let failed = false;
      for (let i = 0; i < allocationIds.length; i++) {
        const res = results[i];
        if (!res?.success) {
          failed = true;
          continue;
        }
        for (const user of res.data) {
          merged.push({ ...user, requestCategoryAllocationId: allocationIds[i] });
        }
      }

      if (failed && merged.length === 0) {
        setLoadState('error');
        return;
      }

      setLoadState('idle');
      setAllocationUsers(merged);
    };

    void load();
    return () => controller.abort();
  }, [
    input.active,
    input.fileCategoryId,
    input.subFileCategoryId,
    input.fileCategoryName,
    input.subFileCategoryName,
    reset,
  ]);

  const counselUsers = useMemo(
    () => filterAssignableCounselUsers(allocationUsers),
    [allocationUsers],
  );

  const options = useMemo<WizardSelectOption[]>(
    () =>
      counselUsers.map((user) => ({
        id: user.userId,
        label: formatCategoryAllocationUserLabel(user),
      })),
    [counselUsers],
  );

  const resolveSelectedCounsel = useCallback((): CategoryAllocationUserRow | null => {
    if (!selectedCounselId) return null;
    return counselUsers.find((user) => user.userId === selectedCounselId) ?? null;
  }, [counselUsers, selectedCounselId]);

  return {
    options,
    loadState,
    selectedCounselId,
    setSelectedCounselId,
    reset,
    resolveSelectedCounsel,
  };
}
